import { Injectable, effect, inject } from '@angular/core';
import { RealtimeHubService } from './realtime-hub.service';
import { HttpCacheService } from './http-cache.service';
import { WhatsAppInboxService } from './whatsapp-inbox.service';

/**
 * Punto UNICO que traduce eventos realtime (SignalR) a invalidaciones de cache.
 *
 * Invariante critica: NO dispara refetch. Solo marca las entradas como viejas para
 * la proxima lectura fria / navegacion. Las pantallas operativas (POS/Cocina/Barra)
 * siguen actualizando en vivo con delta-patching (applyOrderDelta); meterles un
 * refetch por cada delta reintroduciria el "cliente chatty" que es la raiz de la
 * lentitud del POS. Por eso aca solo invalidamos cache, no tocamos esos stores.
 *
 * Se instancia una vez al arranque (ver app.config.ts). Mientras ningun servicio
 * use la cache, las invalidaciones son no-ops inofensivos.
 */
@Injectable({ providedIn: 'root' })
export class CacheInvalidationBridge {
  private hub = inject(RealtimeHubService);
  private cache = inject(HttpCacheService);
  private whatsAppInbox = inject(WhatsAppInboxService);

  constructor() {
    // Ordenes: cualquier alta/baja/cambio (incl. kiosko) invalida el prefijo 'orders'.
    // Tambien invalida 'tables': el backend cambia Table.Status (Occupied/Available) al
    // abrir/cerrar un pedido DineIn, y el POS standard (pos-dashboard) pinta el estado de
    // mesa desde el row CACHEADO de tables.service. Sin esta invalidacion, una mesa ocupada
    // en otra tablet se mostraria libre hasta el TTL. (POS Simple/Mesero derivan de ordenes
    // vivas y no dependen de esto.) Invalidar != refetch: solo marca viejo para la proxima
    // lectura/refresh, no reintroduce el cliente chatty.
    //
    // Tambien invalida 'products': toda venta descuenta stock (y toda cancelacion lo
    // reintegra) en el backend, pero el catalogo del POS lee productos por cache.get$
    // (TTL, ver products.service.getProducts). Sin esto el cajero ve stock ya vendido
    // hasta el TTL -> el cobro falla en backend o vende sin stock. Los mismos eventos
    // de orden que cambian 'orders' son exactamente los que mueven stock por venta, asi
    // que no invalidamos de mas. (El effect de inventario tambien invalida 'products',
    // pero cubre compras/ajustes/transferencias, no la venta directa desde POS.)
    effect(() => {
      this.hub.ordersUpdated();
      this.hub.orderUpserted();
      this.hub.orderDeleted();
      this.hub.kioskOrderCreated();
      this.cache.invalidate('orders');
      this.cache.invalidate('tables');
      this.cache.invalidate('products');
      // Unidades vendidas del turno (vista de caja ciega): cada pedido cobrado las mueve.
      this.cache.invalidate('soldunits');
    });

    // Inventario: afecta stock de productos e insumos. El stock de Supply cambia por
    // compras/ajustes/transferencias que viven en otros servicios; por eso aca se
    // invalida 'supplies' (ademas de products/inventory) para que la lista re-pegue
    // fresca en la proxima lectura.
    effect(() => {
      this.hub.inventoryUpdated();
      this.cache.invalidate('inventory');
      this.cache.invalidate('products');
      this.cache.invalidate('supplies');
    });

    // Caja registradora.
    effect(() => {
      this.hub.cashRegisterUpdated();
      this.cache.invalidate('cashregister');
    });

    // Inbox de WhatsApp (#5): un mensaje entrante/saliente o cambio de no leidos
    // emite ConversationUpserted. Invalida la lista y el hilo para que la proxima
    // lectura traiga el mensaje nuevo y el contador de no leidos al dia. Punto
    // unico: el servicio del inbox solo pulsa el signal, no invalida por su cuenta.
    effect(() => {
      this.whatsAppInbox.conversationUpserted();
      this.cache.invalidate('whatsapp-conversations');
      this.cache.invalidate('whatsapp-thread');
    });
  }
}
